import { Locales } from '@griply/types';
import { NLP } from 'shared/constants';

// These are helpers for building the larger regular expressions for keyword detection.
const monthPatterns: Record<Locales, string> = {
  // eslint-disable-next-line max-len -- regex
  [Locales.EnUS]: `(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)`,
  // eslint-disable-next-line max-len -- regex
  [Locales.NlNL]: `(?:jan(?:uari)?|feb(?:ruari)?|mrt|maart|apr(?:il)?|mei|jun(?:i)?|jul(?:i)?|aug(?:ustus)?|sep(?:tember)?|okt(?:ober)?|nov(?:ember)?|dec(?:ember)?)`,
  // eslint-disable-next-line max-len -- regex
  [Locales.EsES]: `(?:ene(?:ro)?|feb(?:rero)?|mar(?:zo)?|abr(?:il)?|mayo?|jun(?:io)?|jul(?:io)?|ago(?:sto)?|sep(?:tiembre)?|oct(?:ubre)?|nov(?:iembre)?|dic(?:iembre)?)`,
  // eslint-disable-next-line max-len -- regex
  [Locales.DeDE]: `(?:jan(?:uar)?|feb(?:ruar)?|mär(?:z)?|apr(?:il)?|mai|jun(?:i)?|jul(?:i)?|aug(?:ust)?|sep(?:tember)?|okt(?:ober)?|nov(?:ember)?|dez(?:ember)?)`,
};

const dayPatterns: Record<Locales, string> = {
  [Locales.EnUS]: `(?:mon(?:day)?\\b|tue(?:sday)?\\b|wed(?:nesday)?\\b|thu(?:rsday)?\\b|fri(?:day)?\\b|sat(?:urday)?\\b|sun(?:day)?\\b)`,
  [Locales.NlNL]: `(?:ma(?:andag)?\\b|di(?:nsdag)?\\b|wo(?:ensdag)?\\b|do(?:nderdag)?\\b|vr(?:ijdag)?\\b|za(?:terdag)?\\b|zo(?:ndag)?\\b)`,
  // eslint-disable-next-line max-len -- regex
  [Locales.EsES]: `(?:lun(?:es)?\\b|mar(?:tes)?\\b|mié(?:rcoles)?\\b|jue(?:ves)?\\b|vie(?:rnes)?\\b|sáb(?:ado(?:s)?)?\\b|dom(?:ingo(?:s)?)?\\b)`,
  [Locales.DeDE]: `(?:mo(?:ntag)?\\b|di(?:enstag)?\\b|mi(?:ttwoch)?\\b|do(?:nnerstag)?\\b|fr(?:eitag)?\\b|sa(?:mstag)?\\b|so(?:nntag)?\\b)`,
};

// Generates the regex pattern for recurrence rules like "every friday".
export const getRrulePatternContent = (language: Locales) => {
  const k = NLP.rruleKeywords[language] ?? NLP.rruleKeywords[Locales.EnUS];
  const dayPattern = dayPatterns[language];
  const monthPattern = monthPatterns[language];

  // Add 'und' to keywords if it's German, for use in regex patterns
  if (language === Locales.DeDE && !k.und) {
    k.und = 'und';
  }

  // 1. Create a robust pattern for ordinals (e.g., "11.", "12th")
  const ordinalPattern = `\\d{1,2}(?:${k.ordinals})?`;

  // 2. Create a list separator that handles "," and "und" for German
  const dayListSeparator =
    language === Locales.DeDE ? `(?:,\\s*|\\s+${k.und}\\s+)` : `,\\s*`;
  const ordinalListPattern = `${ordinalPattern}(?:${dayListSeparator}${ordinalPattern})*`;

  // 3. Create a pattern for dates that handles ordinals (e.g., "27. Jan", "Dez 31.")
  const spanishSeparator =
    language === Locales.EsES ? '(?:\\s+de)?\\s+' : '\\s+';
  const monthDayPattern = `(?:(?:${monthPattern})${spanishSeparator}${ordinalPattern}|${ordinalPattern}${spanishSeparator}${monthPattern})`;

  const ordinalWordPattern = `(?:${k.first}|${k.second}|${k.third}|${k.fourth}|${k.fifth}|${k.last})`;

  // Re-ordered and enhanced patterns array: most specific matches are now at the top.
  const patterns = [
    // Handles "alle 2 Jahre am 31. Dez"
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+${monthDayPattern}`,
    // Handles "alle 2 Monate am 11. und 12."
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalListPattern}`,
    // Existing patterns (kept for other cases)
    `\\d+\\s+(?:${k.weeks}|${k.week})\\s+${k.on}\\s+(?:${dayPattern}(?:,\\s*${dayPattern})*)`,
    // every 2 months on the 11th, 12th
    `\\d+\\s+(?:${k.months}|${k.month})\\s+${k.on}\\s+${k.the}\\s+${ordinalPattern}(?:,\\s*${ordinalPattern})*`,
    // every [2] month(s) on the first weekday
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.weekday}`,
    // every [2] month(s) on the first weekend
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.weekend}`,
    // every [2] month(s) on the last day
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.day}`,
    // every 2 months on (the) first monday
    `\\d+\\s+(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${dayPattern}`,
    // every 2 months on (the) first monday
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${dayPattern}`,
    // every [2] year(s) on the first monday in jan
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+(?:${dayPattern}|${k.weekday}|${k.weekend})\\s+${k.in}\\s+${monthPattern}`,
    // every [2] year(s) on the first monday
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+(?:${dayPattern}|${k.weekday}|${k.weekend})`,

    // Less specific patterns follow, now with a negative lookahead to prevent partial matches.
    `\\d+\\s+(?:${k.days}|${k.day})(?!\\s+${k.on}\\b)`,
    `\\d+\\s+(?:${k.weeks}|${k.week})(?!\\s+${k.on}\\b)`,
    `\\d+\\s+(?:${k.months}|${k.month}|${k.years}|${k.year})(?!\\s+${k.on}\\b)`, // FIX: Prevents matching "alle 2 Jahre" if "am" follows.

    // Handles "jeden 27. Jan"
    monthDayPattern,
    `${ordinalWordPattern}\\s+${dayPattern}`,
    k.weekday, // every weekday
    `(?:${dayPattern}(?:,\\s*${dayPattern})*)`,
    k.day,
    k.week,
    k.month,
    k.year, // every day, week, etc.
    `(?:${ordinalListPattern})(?=\\s|\\u00A0|\\u200B|$)`, // Use new list pattern for "every 10th, 15th"
  ];

  // the entire pattern is wrapped to ensure it is treated as a single unit,
  // and the trailing word boundary `\b` which caused greedy matching is removed from the master regex.
  // The lookaheads inside the individual patterns now correctly control the match boundaries.
  return `(?:\\b${k.every}\\s+(?:${patterns.join('|')})|\\b${k.daily}\\b|\\b${k.weekly}\\b|\\b${k.monthly}\\b|\\b${k.yearly}\\b)`;
};

/**
 * Parses a recurrence string (e.g., "every fri, mon") into an RRULE string.
 */
export const parseRrule = (rruleStr: string, language: Locales) => {
  if (!rruleStr) {
    return null;
  }
  const originalString = rruleStr;
  // Standardize the input for easier regex matching
  rruleStr = rruleStr.toLowerCase().trim().replace(/\s+/g, ' ');

  const k = NLP.rruleKeywords[language] ?? NLP.rruleKeywords[Locales.EnUS];
  const currentDayMap = NLP.dayMap[language] ?? NLP.dayMap[Locales.EnUS];
  const monthMap = NLP.MONTHS[language];
  const dayPatternStr = dayPatterns[language] ?? dayPatterns[Locales.EnUS];
  const monthPatternStr = monthPatterns[language];

  // Add 'und' to keywords for German regex patterns if it doesn't exist
  if (language === Locales.DeDE && !k.und) {
    k.und = 'und';
  }

  // --- Helper Maps & Patterns ---
  const ordinalWordMap: Record<Locales, Record<string, string>> = {
    [Locales.EnUS]: {
      [k.first]: '+1',
      [k.second]: '+2',
      [k.third]: '+3',
      [k.fourth]: '+4',
      [k.fifth]: '+5',
      [k.last]: '-1',
    },
    [Locales.NlNL]: {
      [k.first]: '+1',
      [k.second]: '+2',
      [k.third]: '+3',
      [k.fourth]: '+4',
      [k.fifth]: '+5',
      [k.last]: '-1',
    },
    [Locales.EsES]: {
      primer: '+1',
      primero: '+1',
      segundo: '+2',
      tercer: '+3',
      tercero: '+3',
      cuarto: '+4',
      quinto: '+5',
      último: '-1',
    },
    [Locales.DeDE]: {
      ersten: '+1',
      zweiten: '+2',
      dritten: '+3',
      vierten: '+4',
      fünften: '+5',
      letzten: '-1',
    },
  };
  const ordinalWordsPatternStr = `(${Object.keys(ordinalWordMap[language]).join('|')})`;
  const spanishSeparator =
    language === Locales.EsES ? '(?:\\s+de)?\\s+' : '\\s+';

  // --- Regex Matching (from most to least specific) ---

  // REGEX 1: Handles "every 2 years on Dec 31."
  const yearlyOnMonthDayRegex = new RegExp(
    // eslint-disable-next-line max-len -- regex
    `^${k.every} (?:(\\d+)\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:(${monthPatternStr})${spanishSeparator}(\\d{1,2}(?:${k.ordinals})?)|(\\d{1,2}(?:${k.ordinals})?)${spanishSeparator}(${monthPatternStr}))$`,
  );
  const yearlyOnMonthDayMatch = rruleStr.match(yearlyOnMonthDayRegex);
  if (yearlyOnMonthDayMatch) {
    const count = yearlyOnMonthDayMatch[1]
      ? parseInt(yearlyOnMonthDayMatch[1], 10)
      : 1;
    const monthName = yearlyOnMonthDayMatch[2] || yearlyOnMonthDayMatch[5];
    const day = yearlyOnMonthDayMatch[3] || yearlyOnMonthDayMatch[4];
    const monthIndex = monthMap[monthName.toLowerCase()];
    if (monthIndex !== undefined && day) {
      const dayInt = parseInt(day, 10);
      const validationDate = new Date(2024, monthIndex, dayInt); // Use a leap year for validation
      if (validationDate.getMonth() !== monthIndex) {
        return null;
      }
      let rrule = `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYMONTHDAY=${dayInt}`;
      if (count > 1) {
        rrule += `;INTERVAL=${count}`;
      }
      return { rrule, rruleString: originalString };
    }
  }

  // REGEX 2: Handles "every 2 months on the 11th and 12th"
  const dayListSeparator =
    language === Locales.DeDE ? `(?:,\\s*|\\s+${k.und}\\s+)` : `,\\s*`;
  const ordinalPattern = `\\d{1,2}(?:${k.ordinals})?`;
  const dayListPattern = `${ordinalPattern}(?:${dayListSeparator}${ordinalPattern})*`;
  const monthlyOnDayNumberRegex = new RegExp(
    `^${k.every} (?:(\\d+)\\s+)?(${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?(${dayListPattern})$`,
  );
  const monthlyOnDayNumberMatch = rruleStr.match(monthlyOnDayNumberRegex);
  if (monthlyOnDayNumberMatch) {
    const count = monthlyOnDayNumberMatch[1]
      ? parseInt(monthlyOnDayNumberMatch[1], 10)
      : 1;
    // Normalize the list to be comma-separated for consistent parsing
    const daysStr =
      language === Locales.DeDE
        ? monthlyOnDayNumberMatch[3].replace(/\s+und\s+/g, ',')
        : monthlyOnDayNumberMatch[3];

    const originalDayParts = daysStr.split(',').map((d) => d.trim());
    const validDays = originalDayParts
      .map((d) => parseInt(d, 10))
      .filter((d) => !isNaN(d) && d > 0 && d < 32);

    if (validDays.length > 0 && validDays.length === originalDayParts.length) {
      let rrule = `FREQ=MONTHLY;BYMONTHDAY=${validDays.join(',')}`;
      if (count > 1) {
        rrule += `;INTERVAL=${count}`;
      }
      return { rrule, rruleString: originalString };
    }
  }

  // REGEX 3: Handles "every 2 months on the second tuesday"
  const monthlyOnOrdinalDayRegex = new RegExp(
    `^${k.every} (?:(\\d+)\\s+)?(${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s+)?${ordinalWordsPatternStr}\\s+(${dayPatternStr})$`,
    'i',
  );
  const monthlyOnOrdinalDayMatch = rruleStr.match(monthlyOnOrdinalDayRegex);
  if (monthlyOnOrdinalDayMatch) {
    const count = monthlyOnOrdinalDayMatch[1]
      ? parseInt(monthlyOnOrdinalDayMatch[1], 10)
      : 1;
    const ordinalWord = monthlyOnOrdinalDayMatch[3]; // Corrected index
    const dayWord = monthlyOnOrdinalDayMatch[4]; // Corrected index
    const ordinalValue = ordinalWordMap[language][ordinalWord];
    const dayValueKey = Object.keys(currentDayMap).find((d) =>
      dayWord.startsWith(d),
    );
    if (ordinalValue && dayValueKey) {
      const rruleDay = currentDayMap[dayValueKey];
      let rrule = `FREQ=MONTHLY;BYDAY=${ordinalValue}${rruleDay}`;
      if (count > 1) {
        rrule += `;INTERVAL=${count}`;
      }
      return { rrule, rruleString: originalString };
    }
  }

  // --- Other complex rules (many of these were already correct) ---
  // (Your existing regexes for intervalOnDay, yearlyIntervalOnOrdinalDay, etc., go here. The following are the most relevant ones.)

  const intervalOnDayRegex = new RegExp(
    `^${k.every} (\\d+) (${k.weeks}|${k.week})\\s+${k.on}\\s+((?:${dayPatternStr}(?:,\\s*${dayPatternStr})*))$`,
    'i',
  );
  const intervalOnDayMatch = rruleStr.match(intervalOnDayRegex);
  if (intervalOnDayMatch) {
    const count = parseInt(intervalOnDayMatch[1], 10);
    const daysStr = intervalOnDayMatch[3];
    const dayRegexInternal = new RegExp(dayPatternStr, 'g');
    const dayMatches = [...daysStr.matchAll(dayRegexInternal)]
      .map((m) => currentDayMap[m[0].toLowerCase()])
      .filter(Boolean);
    if (dayMatches.length > 0) {
      return {
        rrule: `FREQ=WEEKLY;INTERVAL=${count};BYDAY=${dayMatches.join(',')}`,
        rruleString: originalString,
      };
    }
  }

  // Handles "jedes Jahr am ersten Montag im Feb"
  const yearlyIntervalOnOrdinalDayInMonthRegex = new RegExp(
    // eslint-disable-next-line max-len -- regex
    `^${k.every} (?:(\\d+)\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordsPatternStr}\\s+(${dayPatternStr}|${k.weekday}|${k.weekend})\\s+${k.in}\\s+(${monthPatternStr})$`,
  );
  const yearlyIntervalOnOrdinalDayInMonthMatch = rruleStr.match(
    yearlyIntervalOnOrdinalDayInMonthRegex,
  );
  if (yearlyIntervalOnOrdinalDayInMonthMatch) {
    const count = yearlyIntervalOnOrdinalDayInMonthMatch[1]
      ? parseInt(yearlyIntervalOnOrdinalDayInMonthMatch[1], 10)
      : 1;
    const ordinalWord = yearlyIntervalOnOrdinalDayInMonthMatch[2];
    const dayWord = yearlyIntervalOnOrdinalDayInMonthMatch[3].toLowerCase();
    const monthWord = yearlyIntervalOnOrdinalDayInMonthMatch[4].toLowerCase();
    const ordinalValue = Object.entries(ordinalWordMap[language]).find(
      ([key]) => ordinalWord.startsWith(key),
    )?.[1];
    const monthIndex = monthMap[monthWord];

    if (ordinalValue && monthIndex !== undefined) {
      let rrule;
      if (new RegExp(`^${k.weekday}$`).test(dayWord)) {
        rrule = `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYDAY=MO,TU,WE,TH,FR;BYSETPOS=${ordinalValue.replace('+', '')}`;
      } else if (new RegExp(`^${k.weekend}$`).test(dayWord)) {
        rrule = `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYDAY=SA,SU;BYSETPOS=${ordinalValue.replace('+', '')}`;
      } else {
        const dayValueKey = Object.keys(currentDayMap).find((d) =>
          dayWord.startsWith(d),
        );
        if (dayValueKey) {
          rrule = `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYDAY=${ordinalValue}${currentDayMap[dayValueKey]}`;
        }
      }
      if (rrule) {
        if (count > 1) {
          rrule += `;INTERVAL=${count}`;
        }
        return { rrule, rruleString: originalString };
      }
    }
  }

  // Handles "jedes Jahr am ersten Montag"
  const yearlyIntervalOnOrdinalDayRegex = new RegExp(
    // eslint-disable-next-line max-len -- regex
    `^${k.every} (?:(\\d+)\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordsPatternStr}\\s+(${dayPatternStr}|${k.weekday}|${k.weekend})$`,
  );
  const yearlyIntervalOnOrdinalDayMatch = rruleStr.match(
    yearlyIntervalOnOrdinalDayRegex,
  );
  if (yearlyIntervalOnOrdinalDayMatch) {
    const count = yearlyIntervalOnOrdinalDayMatch[1]
      ? parseInt(yearlyIntervalOnOrdinalDayMatch[1], 10)
      : 1;
    const ordinalWord = yearlyIntervalOnOrdinalDayMatch[2];
    const dayWord = yearlyIntervalOnOrdinalDayMatch[3].toLowerCase();
    const ordinalValue = Object.entries(ordinalWordMap[language]).find(
      ([key]) => ordinalWord.startsWith(key),
    )?.[1];
    if (ordinalValue) {
      let rrule;
      if (new RegExp(`^${k.weekday}$`).test(dayWord)) {
        rrule = `FREQ=YEARLY;BYDAY=MO,TU,WE,TH,FR;BYSETPOS=${ordinalValue.replace('+', '')}`;
      } else if (new RegExp(`^${k.weekend}$`).test(dayWord)) {
        rrule = `FREQ=YEARLY;BYDAY=SA,SU;BYSETPOS=${ordinalValue.replace('+', '')}`;
      } else {
        const dayValueKey = Object.keys(currentDayMap).find((d) =>
          dayWord.startsWith(d),
        );
        if (dayValueKey) {
          rrule = `FREQ=YEARLY;BYDAY=${ordinalValue}${currentDayMap[dayValueKey]}`;
        }
      }
      if (rrule) {
        if (count > 1) {
          rrule += `;INTERVAL=${count}`;
        }
        return { rrule, rruleString: originalString };
      }
    }
  }

  // Handles "jeden Monat am ersten Wochentag"
  const monthlyOnOrdinalWeekendOrWeekdayRegex = new RegExp(
    // eslint-disable-next-line max-len -- regex
    `^${k.every} (?:(\\d+)\\s+)?(?:${k.months}|${k.month}) ${k.on} (?:${k.the}\\s*)?\\s*${ordinalWordsPatternStr}\\s+(${k.weekday}|${k.weekend})$`,
  );
  const monthlyOnOrdinalWeekendOrWeekdayMatch = rruleStr.match(
    monthlyOnOrdinalWeekendOrWeekdayRegex,
  );
  if (monthlyOnOrdinalWeekendOrWeekdayMatch) {
    const count = monthlyOnOrdinalWeekendOrWeekdayMatch[1]
      ? parseInt(monthlyOnOrdinalWeekendOrWeekdayMatch[1], 10)
      : 1;
    const ordinalWord = monthlyOnOrdinalWeekendOrWeekdayMatch[2];
    const dayWord = monthlyOnOrdinalWeekendOrWeekdayMatch[3];
    const ordinalValue = Object.entries(ordinalWordMap[language]).find(
      ([key]) => ordinalWord.startsWith(key),
    )?.[1];

    if (ordinalValue) {
      let byDayValue = '';
      // We must test 'dayWord' against the regex patterns, not compare it as a string.
      if (new RegExp(`^${k.weekday}$`).test(dayWord)) {
        byDayValue = 'MO,TU,WE,TH,FR';
      } else if (new RegExp(`^${k.weekend}$`).test(dayWord)) {
        byDayValue = 'SA,SU';
      }

      if (byDayValue) {
        let rrule = `FREQ=MONTHLY;BYDAY=${byDayValue};BYSETPOS=${ordinalValue.replace('+', '')}`;
        if (count > 1) {
          rrule += `;INTERVAL=${count}`;
        }
        return { rrule, rruleString: originalString };
      }
    }
  }

  // Handles "every month on the last day" (FREQ=MONTHLY;BYMONTHDAY=-1)
  const monthlyOnOrdinalDayOfMonthRegex = new RegExp(
    `^${k.every} (?:(\\d+)\\s+)?(?:${k.months}|${k.month}) ${k.on} (?:${k.the}\\s*)?\\s*${ordinalWordsPatternStr}\\s+${k.day}$`,
  );
  const monthlyOnOrdinalDayOfMonthMatch = rruleStr.match(
    monthlyOnOrdinalDayOfMonthRegex,
  );
  if (monthlyOnOrdinalDayOfMonthMatch) {
    const count = monthlyOnOrdinalDayOfMonthMatch[1]
      ? parseInt(monthlyOnOrdinalDayOfMonthMatch[1], 10)
      : 1;
    const ordinalWord = monthlyOnOrdinalDayOfMonthMatch[2];
    const ordinalValue = Object.entries(ordinalWordMap[language]).find(
      ([key]) => ordinalWord.startsWith(key),
    )?.[1];

    if (ordinalValue) {
      const byMonthDay = parseInt(ordinalValue, 10);
      let rrule = `FREQ=MONTHLY;BYMONTHDAY=${byMonthDay}`;
      if (count > 1) {
        rrule += `;INTERVAL=${count}`;
      }
      return { rrule, rruleString: originalString };
    }
  }

  // --- Simple interval rules ---
  const intervalRegex = new RegExp(
    `${k.every} (\\d+) (${k.days}|${k.day}|${k.weeks}|${k.week}|${k.months}|${k.month}|${k.years}|${k.year})$`,
  );
  const intervalMatch = rruleStr.match(intervalRegex);
  if (intervalMatch) {
    const count = parseInt(intervalMatch[1], 10);
    const unit = intervalMatch[2];
    const now = new Date();
    let rrule = '';
    if (unit === k.day || unit === k.days) {
      rrule = `FREQ=DAILY;INTERVAL=${count}`;
    } else if (unit === k.week || unit === k.weeks) {
      const dayOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][
        now.getDay()
      ];
      rrule = `FREQ=WEEKLY;INTERVAL=${count};BYDAY=${dayOfWeek}`;
    } else if (unit === k.month || unit === k.months) {
      const dayOfMonth = now.getDate();
      rrule = `FREQ=MONTHLY;INTERVAL=${count};BYMONTHDAY=${dayOfMonth}`;
    } else if (unit === k.year || unit === k.years) {
      const month = now.getMonth() + 1;
      const dayOfMonth = now.getDate();
      rrule = `FREQ=YEARLY;INTERVAL=${count};BYMONTH=${month};BYMONTHDAY=${dayOfMonth}`;
    }
    if (rrule) {
      return { rrule, rruleString: originalString };
    }
  }

  // --- Correctly test simple frequency keywords ---
  if (
    new RegExp(`^${k.daily}$`).test(rruleStr) ||
    new RegExp(`^${k.every} ${k.day}$`).test(rruleStr)
  ) {
    return { rrule: 'FREQ=DAILY', rruleString: originalString };
  }
  if (new RegExp(`^${k.every} ${k.weekday}$`).test(rruleStr)) {
    return {
      rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR',
      rruleString: originalString,
    };
  }

  if (
    new RegExp(`^${k.weekly}$`).test(rruleStr) ||
    new RegExp(`^${k.every} ${k.week}$`).test(rruleStr)
  ) {
    const now = new Date();
    const dayOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][now.getDay()];
    return {
      rrule: `FREQ=WEEKLY;BYDAY=${dayOfWeek}`,
      rruleString: originalString,
    };
  }
  if (
    new RegExp(`^${k.monthly}$`).test(rruleStr) ||
    new RegExp(`^${k.every} ${k.month}$`).test(rruleStr)
  ) {
    const now = new Date();
    const dayOfMonth = now.getDate();
    return {
      rrule: `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth}`,
      rruleString: originalString,
    };
  }
  if (
    new RegExp(`^${k.yearly}$`).test(rruleStr) ||
    new RegExp(`^${k.every} ${k.year}$`).test(rruleStr)
  ) {
    const now = new Date();
    const month = now.getMonth() + 1;
    const dayOfMonth = now.getDate();
    return {
      rrule: `FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${dayOfMonth}`,
      rruleString: originalString,
    };
  }

  // --- Simple yearly date rules ---
  // REGEX 4: Handles "every Jan 27." or "every 27. Jan"
  const yearlyDateRegex = new RegExp(
    // eslint-disable-next-line max-len -- regex
    `^${k.every} (?:(${monthPatternStr})${spanishSeparator}(\\d{1,2}(?:${k.ordinals})?)|(\\d{1,2}(?:${k.ordinals})?)${spanishSeparator}(${monthPatternStr}))$`,
  );
  const yearlyDateMatch = rruleStr.match(yearlyDateRegex);
  if (yearlyDateMatch) {
    const monthName = yearlyDateMatch[1] || yearlyDateMatch[4];
    const day = yearlyDateMatch[2] || yearlyDateMatch[3];
    const monthIndex = monthMap[monthName.toLowerCase()];
    if (monthIndex !== undefined && day) {
      const dayInt = parseInt(day, 10);
      const validationDate = new Date(2024, monthIndex, dayInt);
      if (validationDate.getMonth() === monthIndex) {
        return {
          rrule: `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYMONTHDAY=${dayInt}`,
          rruleString: originalString,
        };
      }
    }
  }

  // REGEX 5: Handles weekly day lists like "every monday, friday"
  const weeklyOnDaysRegex = new RegExp(
    `^${k.every}\\s+((?:${dayPatternStr}(?:,\\s*${dayPatternStr})*))$`,
  );
  const weeklyOnDaysMatch = rruleStr.match(weeklyOnDaysRegex);
  if (weeklyOnDaysMatch) {
    const dayMatches = [
      ...weeklyOnDaysMatch[1].matchAll(new RegExp(dayPatternStr, 'g')),
    ]
      .map((m) => currentDayMap[m[0].toLowerCase()])
      .filter(Boolean);
    if (dayMatches.length > 0) {
      return {
        rrule: `FREQ=WEEKLY;BYDAY=${dayMatches.join(',')}`,
        rruleString: originalString,
      };
    }
  }

  // REGEX 6: Handles "jeden ersten Montag" (every first Monday)
  const monthlyOrdinalDaySimpleRegex = new RegExp(
    `^${k.every}\\s+${ordinalWordsPatternStr}\\s+(${dayPatternStr})$`,
  );
  const monthlyOrdinalDaySimpleMatch = rruleStr.match(
    monthlyOrdinalDaySimpleRegex,
  );
  if (monthlyOrdinalDaySimpleMatch) {
    const ordinalWord = monthlyOrdinalDaySimpleMatch[1];
    const dayWord = monthlyOrdinalDaySimpleMatch[2];

    // --- START OF FIX ---
    // Use a direct, exact lookup for the ordinal word.
    const ordinalValue = ordinalWordMap[language][ordinalWord];
    const dayValueKey = Object.keys(currentDayMap).find((d) =>
      dayWord.startsWith(d),
    );

    if (ordinalValue && dayValueKey) {
      const rruleDay = currentDayMap[dayValueKey];
      return {
        rrule: `FREQ=MONTHLY;BYDAY=${ordinalValue}${rruleDay}`,
        rruleString: originalString,
      };
    }
    // --- END OF FIX ---
  }

  // REGEX 7: Handles "jeden 28." or "jeden 10., 15."
  const monthlyDayRegex = new RegExp(
    `^${k.every}\\s+(${dayListPattern})(?!${spanishSeparator}${monthPatternStr})`,
  );
  const monthlyDayMatch = rruleStr.match(monthlyDayRegex);
  if (monthlyDayMatch) {
    const daysStr =
      language === Locales.DeDE
        ? monthlyDayMatch[1].replace(/\s+und\s+/g, ',')
        : monthlyDayMatch[1];
    const originalDayParts = daysStr.split(',').map((d) => d.trim());
    const validDays = originalDayParts
      .map((d) => parseInt(d, 10))
      .filter((d) => !isNaN(d) && d > 0 && d < 32);

    if (validDays.length && validDays.length === originalDayParts.length) {
      return {
        rrule: `FREQ=MONTHLY;BYMONTHDAY=${validDays.join(',')}`,
        rruleString: originalString,
      };
    }
  }

  // Fallback if no specific pattern matched
  return null;
};

/**
 * Converts an RRULE string into a natural language phrase.
 * This is a simplified implementation focusing on common cases.
 */
export const parseRruleToString = (rrule: string, language: Locales) => {
  if (!rrule) {
    return '';
  }

  const parts = rrule
    .toUpperCase()
    .replace(/^RRULE:/, '')
    .split(';')
    .reduce<Record<string, string>>((acc, part) => {
      const [key, value] = part.split('=');
      if (key && value) {
        acc[key] = value.trim();
      }
      return acc;
    }, {});

  const k = NLP.rruleKeywords[language] ?? NLP.rruleKeywords[Locales.EnUS];
  const dayMapReverse = Object.entries(
    NLP.dayMap[language] ?? NLP.dayMap[Locales.EnUS],
  ).reduce<Record<string, string>>((acc, [key, val]) => {
    // Prefer longer keys for reverse mapping e.g. 'monday' for 'MO' instead of 'mon'
    if (!acc[val] || key.length > acc[val].length) {
      acc[val] = key;
    }
    return acc;
  }, {});
  const monthMapReverse = Object.entries(
    NLP.MONTHS[language] ?? NLP.MONTHS[Locales.EnUS],
  ).reduce<Record<string, string>>((acc, [key, val]) => {
    if (!acc[val] || key.length < acc[val].length) {
      acc[val] = key;
    }
    return acc;
  }, {});

  // Reverse map for ordinal words from RRULE values (+1, -1) to natural language ('first', 'last')
  const ordinalValueMapReverse = {
    '+1': k.first_simple,
    '+2': k.second_simple,
    '+3': k.third_simple,
    '+4': k.fourth_simple,
    '+5': k.fifth_simple,
    '-1': k.last_simple,
  };

  const { FREQ, BYDAY, INTERVAL, BYMONTHDAY, BYMONTH, BYSETPOS } = parts;
  // Helper to add "the" only for EN/NL
  const theArticle = [Locales.EnUS, Locales.NlNL].includes(language)
    ? `${k.the} `
    : '';

  // Handle "every 2 weeks on fri" (FREQ=WEEKLY;INTERVAL=2;BYDAY=FR)
  if (FREQ === 'WEEKLY' && INTERVAL && BYDAY) {
    const count = parseInt(INTERVAL, 10);
    if (count > 1) {
      const days = BYDAY.split(',')
        .map((d) => dayMapReverse[d.trim()])
        .filter(Boolean)
        .join(', ');
      if (days) {
        return `${k.every_simple} ${count} ${k.weeks} ${k.on_simple} ${days}`;
      }
    }
  }

  // Handle "every 2 months on the first monday" (FREQ=MONTHLY;BYDAY=+1MO)
  if (FREQ === 'MONTHLY' && BYDAY) {
    // Handle "every month on the first weekend" (FREQ=MONTHLY;BYDAY=SA,SU;BYSETPOS=1)
    if (BYSETPOS) {
      const bydayTokens = BYDAY.split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .sort(); // Normalize order e.g., SA,SU
      const bydayValue = [...bydayTokens].join(',');
      const setpos = parseInt(BYSETPOS, 10);
      const ordinalKey = setpos > 0 ? `+${setpos}` : `${setpos}`;
      const ordinalWord =
        ordinalValueMapReverse[
          ordinalKey as keyof typeof ordinalValueMapReverse
        ];

      if (ordinalWord) {
        // Single specific day like BYDAY=MO with BYSETPOS=2
        if (bydayTokens.length === 1) {
          const dayWordSingle = dayMapReverse[bydayTokens[0]];
          if (dayWordSingle) {
            const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
            const monthTerm = count > 1 ? k.months : k.month;
            const intervalTerm = count > 1 ? `${count} ` : '';
            return `${k.every_simple} ${intervalTerm}${monthTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayWordSingle}`;
          }
        }

        let dayTerm = '';
        // Normalized alphabetical order for weekdays
        const weekdayValue = ['MO', 'TU', 'WE', 'TH', 'FR'].sort().join(',');

        if (bydayValue === 'SA,SU') {
          dayTerm = k.weekend_simple;
        } else if (bydayValue === weekdayValue) {
          dayTerm = k.weekday_simple;
        }

        if (dayTerm) {
          const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
          const monthTerm = count > 1 ? k.months : k.month;
          const intervalTerm = count > 1 ? `${count} ` : '';
          return `${k.every_simple} ${intervalTerm}${monthTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayTerm}`;
        }
      }
    }

    const byDayMatch = BYDAY.match(/([+-]?\d+)([A-Z]{2})/);
    if (byDayMatch) {
      const ordinalPart = byDayMatch[1];
      const dayPart = byDayMatch[2];

      const ordinalWord =
        ordinalValueMapReverse[
          ordinalPart as keyof typeof ordinalValueMapReverse
        ];
      const dayWord = dayMapReverse[dayPart];

      if (ordinalWord && dayWord) {
        const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
        const monthTerm = count > 1 ? k.months : k.month;
        const intervalTerm = count > 1 ? `${count} ` : '';
        return `${k.every_simple} ${intervalTerm}${monthTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayWord}`;
      }
    }
  }

  // Handle "every last day of the month" (FREQ=MONTHLY;BYMONTHDAY=-1)
  if (FREQ === 'MONTHLY' && BYMONTHDAY === '-1' && !BYDAY) {
    const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
    const monthTerm = count > 1 ? k.months : k.month;
    const intervalTerm = count > 1 ? `${count} ` : '';
    return `${k.every_simple} ${intervalTerm}${monthTerm} ${k.on_simple} ${theArticle}${k.last_simple} ${k.day}`;
  }

  // Handle "every 8th" (FREQ=MONTHLY;BYMONTHDAY=8) or "every 2 months on the 8th"
  if (FREQ === 'MONTHLY' && BYMONTHDAY && !BYDAY) {
    const getOrdinalSuffix = (nStr: string, lang: Locales) => {
      const n = parseInt(nStr, 10);
      if (lang === Locales.NlNL) {
        return 'e'; // Using a simple suffix for Dutch for now.
      }
      if (lang === Locales.DeDE) {
        return '.'; // Using a simple suffix for German.
      }
      if (lang === Locales.EsES) {
        return 'º'; // Using a simple suffix for Spanish.
      }
      // English
      if (n >= 11 && n <= 13) {
        return 'th';
      }
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    const days = BYMONTHDAY.split(',')
      .map((d) => d + getOrdinalSuffix(d, language))
      .join(', ');

    if (INTERVAL) {
      const count = parseInt(INTERVAL, 10);
      if (count > 1) {
        return `${k.every_simple} ${count} ${k.months} ${k.on_simple} ${theArticle}${days}`;
      }
    }
    return `${k.every_simple} ${days}`;
  }

  // Handle "every 2 years on the first monday" (FREQ=YEARLY;BYDAY=+1MO;INTERVAL=2)
  if (FREQ === 'YEARLY' && BYDAY && !BYMONTH) {
    // Check for !BYMONTH to avoid clashes with more specific rules like "in Jan"
    const byDayMatch = BYDAY.match(/([+-]?\d+)([A-Z]{2})/);
    if (byDayMatch) {
      const ordinalPart = byDayMatch[1];
      const dayPart = byDayMatch[2];

      const ordinalWord =
        ordinalValueMapReverse[
          ordinalPart as keyof typeof ordinalValueMapReverse
        ];
      const dayWord = dayMapReverse[dayPart];

      if (ordinalWord && dayWord) {
        const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
        const yearTerm = count > 1 ? k.years : k.year;
        const intervalTerm = count > 1 ? `${count} ` : '';
        return `${k.every_simple} ${intervalTerm}${yearTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayWord}`;
      }
    }
  }

  // Handle "every year on the first Monday in February" (FREQ=YEARLY;BYMONTH=2;BYDAY=+1MO)
  if (FREQ === 'YEARLY' && BYDAY && BYMONTH && !BYSETPOS && !BYMONTHDAY) {
    const byDayMatch = BYDAY.match(/([+-]?\d+)([A-Z]{2})/);
    const monthIndex = parseInt(BYMONTH, 10) - 1;
    const monthName = monthMapReverse[monthIndex];

    if (byDayMatch && monthName) {
      const ordinalPart = byDayMatch[1];
      const dayPart = byDayMatch[2];

      const ordinalWord =
        ordinalValueMapReverse[
          ordinalPart as keyof typeof ordinalValueMapReverse
        ];
      const dayWord = dayMapReverse[dayPart];

      if (ordinalWord && dayWord) {
        const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
        const yearTerm = count > 1 ? k.years : k.year;
        const intervalTerm = count > 1 ? `${count} ` : '';
        // eslint-disable-next-line max-len -- long output line
        return `${k.every_simple} ${intervalTerm}${yearTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayWord} ${k.in_simple} ${monthName}`;
      }
    }
  }

  // Handle "every year on the fifth weekend in January" (FREQ=YEARLY;BYMONTH=1;BYDAY=SA,SU;BYSETPOS=5)
  if (FREQ === 'YEARLY' && BYDAY && BYMONTH && BYSETPOS && !BYMONTHDAY) {
    const bydayValue = BYDAY.split(',')
      .map((s) => s.trim())
      .sort()
      .join(','); // Normalize order e.g., SA,SU
    const setpos = parseInt(BYSETPOS, 10);
    const ordinalKey = setpos > 0 ? `+${setpos}` : `${setpos}`;
    const ordinalWord =
      ordinalValueMapReverse[ordinalKey as keyof typeof ordinalValueMapReverse];
    const monthIndex = parseInt(BYMONTH, 10) - 1;
    const monthName = monthMapReverse[monthIndex];

    if (ordinalWord && monthName) {
      let dayTerm = '';
      // Normalized alphabetical order for weekdays
      const weekdayValue = ['MO', 'TU', 'WE', 'TH', 'FR'].sort().join(',');

      if (bydayValue === 'SA,SU') {
        dayTerm = k.weekend_simple;
      } else if (bydayValue === weekdayValue) {
        dayTerm = k.weekday_simple;
      } else {
        // Handle a single day like BYDAY=MO, giving "first monday in jan"
        const dayWord = dayMapReverse[bydayValue];
        if (dayWord) {
          dayTerm = dayWord;
        }
      }

      if (dayTerm) {
        const count = INTERVAL ? parseInt(INTERVAL, 10) : 1;
        const yearTerm = count > 1 ? k.years : k.year;
        const intervalTerm = count > 1 ? `${count} ` : '';
        // eslint-disable-next-line max-len -- long output line
        return `${k.every_simple} ${intervalTerm}${yearTerm} ${k.on_simple} ${theArticle}${ordinalWord} ${dayTerm} ${k.in_simple} ${monthName}`;
      }
    }
  }

  // Handle "every jan 28" (FREQ=YEARLY;BYMONTH=1;BYMONTHDAY=28)
  if (FREQ === 'YEARLY' && BYMONTH && BYMONTHDAY) {
    const monthIndex = parseInt(BYMONTH, 10) - 1;
    const monthName = monthMapReverse[monthIndex];
    const day = BYMONTHDAY;

    if (monthName) {
      // Handle interval, e.g., "every 2 years on jan 28"
      if (INTERVAL) {
        const count = parseInt(INTERVAL, 10);
        if (count > 1) {
          return `${k.every_simple} ${count} ${k.years} ${k.on_simple} ${monthName} ${day}`;
        }
      }
      return `${k.every_simple} ${monthName} ${day}`;
    }
  }

  // Handle intervals first, as they are more specific
  if (INTERVAL) {
    const count = parseInt(INTERVAL, 10);
    if (count > 1) {
      if (FREQ === 'DAILY') {
        return `${k.every_simple} ${count} ${k.days}`;
      }
      if (FREQ === 'WEEKLY') {
        return `${k.every_simple} ${count} ${k.weeks}`;
      }
      if (FREQ === 'MONTHLY') {
        return `${k.every_simple} ${count} ${k.months}`;
      }
      if (FREQ === 'YEARLY') {
        return `${k.every_simple} ${count} ${k.years}`;
      }
    }
  }

  // Handle common weekly patterns, including FREQ=DAILY with BYDAY.
  if ((FREQ === 'DAILY' || FREQ === 'WEEKLY') && BYDAY) {
    const days = BYDAY.split(',')
      .map((d) => dayMapReverse[d.trim()])
      .filter(Boolean)
      .join(', ');
    if (days) {
      return `${k.every_simple} ${days}`;
    }
  }

  if (FREQ === 'DAILY' && !BYDAY) {
    return k.daily;
  }
  if (FREQ === 'WEEKLY' && !BYDAY) {
    return k.weekly;
  }
  if (FREQ === 'MONTHLY' && !BYDAY) {
    return k.monthly;
  }
  if (FREQ === 'YEARLY' && !BYDAY) {
    return k.yearly;
  }

  // Fallback: if we can't parse it nicely, just return the original string.
  return rrule;
};
